import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import {
  addDays,
  formatDateToYMD,
  parseYMDToDate,
  startOfDay
} from '../utils/metricDateUtils'

const DaySelector = ({ selectedDay, onDayChange }) => {
  const { user } = useAuth()
  const { t, i18n } = useTranslation(['dashboard', 'common'])
  const [availableData, setAvailableData] = useState(new Set())
  const [loading, setLoading] = useState(false)

  const today = useMemo(() => startOfDay(new Date()), [])

  useEffect(() => {
    fetchAvailableData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const fetchAvailableData = async () => {
    if (!user) return

    try {
      setLoading(true)

      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (!business) return

      const { data: metrics } = await supabase
        .from('daily_metrics')
        .select('metric_date')
        .eq('business_id', business.id)

      const keys = new Set(metrics?.map((m) => m.metric_date) || [])
      setAvailableData(keys)
    } catch (error) {
      console.error('Error fetching daily available data:', error)
    } finally {
      setLoading(false)
    }
  }

  const hasData = (day) => {
    return availableData.has(formatDateToYMD(day))
  }

  const navigateDay = (direction) => {
    const delta = direction === 'prev' ? -1 : 1
    onDayChange(addDays(selectedDay, delta))
  }

  const handleDateInputChange = (e) => {
    const d = parseYMDToDate(e.target.value)
    if (!d) return
    onDayChange(startOfDay(d))
  }

  const nextDisabled = formatDateToYMD(selectedDay) >= formatDateToYMD(today)

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-5">
        <h3 className="text-lg font-semibold tracking-tight text-gray-900">{t('customer.selectDay')}</h3>
        <div className="flex items-center gap-2 text-xs sm:text-sm">
          {loading ? (
            <span className="text-gray-500">{t('status.loading', { ns: 'common' })}</span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-green-700">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-green-600" />
                {t('dataEntry.monthSelector.hasData')}
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-gray-500">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-gray-400" />
                {t('dataEntry.monthSelector.noData')}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <button
          onClick={() => navigateDay('prev')}
          className="flex items-center flex-shrink-0 px-2 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF1B54]"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('buttons.previous', { ns: 'common' })}
        </button>

        <div className="flex items-center justify-center gap-1 flex-1 min-w-[8rem] px-1">
          {hasData(selectedDay) ? (
            <span className="inline-flex items-center text-green-600 flex-shrink-0">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          ) : null}

          <span className="text-sm font-semibold text-gray-900 text-center leading-tight whitespace-normal">
            {selectedDay.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </span>
        </div>

        <button
          onClick={() => navigateDay('next')}
          disabled={nextDisabled}
          className="flex items-center flex-shrink-0 px-2 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF1B54] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('buttons.next', { ns: 'common' })}
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div>
        <label htmlFor="metric-date" className="block text-sm font-semibold text-gray-700 mb-2">
          {t('common.date', { ns: 'common' })}
        </label>
        <input
          id="metric-date"
          type="date"
          value={formatDateToYMD(selectedDay)}
          max={formatDateToYMD(today)}
          onChange={handleDateInputChange}
          className="block w-full h-11 border border-gray-300 rounded-lg px-3.5 py-2.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:border-[#FF1B54]"
        />
      </div>

      <div className="mt-5 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-500 mb-3">{t('customer.quickJump')}</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onDayChange(today)}
            className="px-3 py-1.5 text-sm font-medium text-[#FF1B54] bg-[#FFF0F4] border border-[#FFC7D6] rounded-lg hover:bg-[#FFDCE7] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-1"
          >
            {t('customer.today')}
          </button>
          <button
            onClick={() => onDayChange(addDays(today, -1))}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-1"
          >
            {t('customer.yesterday')}
          </button>
        </div>
      </div>

      {hasData(selectedDay) && (
        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
          {t('customer.dayDataAvailable')}
        </div>
      )}
    </div>
  )
}

export default DaySelector
